/**
 * Format a timestamp to a human-readable date and time
 * @param timestamp Unix timestamp in seconds or milliseconds
 * @returns Formatted date string
 */
export const formatTimestamp = (timestamp: number): string => {
  if (!timestamp) return 'N/A';
  
  // Convert to milliseconds if in seconds
  const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  
  try {
    const date = new Date(timestampMs);
    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
};

/**
 * Format a duration in milliseconds to a human-readable string
 * @param ms Duration in milliseconds
 * @returns Formatted duration string
 */
export const formatDuration = (ms: number): string => {
  if (!ms || isNaN(ms)) return 'N/A';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  
  return `${seconds}s`;
};

/**
 * Get a relative time string (e.g., "2 hours ago")
 * @param timestamp Unix timestamp in seconds or milliseconds
 * @returns Relative time string
 */
export const getRelativeTime = (timestamp: number): string => {
  if (!timestamp) return 'N/A';
  
  // Convert to milliseconds if in seconds
  const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  
  try {
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    const now = Date.now();
    const diff = timestampMs - now;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (Math.abs(days) > 0) {
      return rtf.format(days, 'day');
    }
    
    if (Math.abs(hours) > 0) {
      return rtf.format(hours, 'hour');
    }
    
    if (Math.abs(minutes) > 0) {
      return rtf.format(minutes, 'minute');
    }
    
    return rtf.format(seconds, 'second');
  } catch (error) {
    console.error('Error getting relative time:', error);
    return formatTimestamp(timestamp);
  }
};
